import type { DailyCheckInDay, DailyCheckInRound } from './types';

/**
 * 회차에서 "오늘" 칸의 **배열 위치**. 못 정하면 `undefined`.
 *
 * **INVARIANT — 기기 시계를 읽지 않는다.** 기기 날짜(`day.date === today`)로 맞추면 서버 시계가
 * 앞서 있는 개발 백엔드에서는 7일 중 어느 것과도 안 맞아 오늘 칸이 통째로 사라진다(7칸이 전부
 * `upcoming`). 기기 시각 오설정·해외 사용자도 같은 부류다. 둘 다 서버가 알려준 값이고,
 * 클라이언트 추측은 없다.
 *
 * 1. **`round.todayDayIndex`** — `GET current-round` 가 주는 오늘 일차. 1순위다. 배열 위치는
 *    `dayIndex` 로 **찾아서** 얻는다 — `todayDayIndex - 1` 로 계산하지 않는다(서버가 번호를
 *    건너뛰거나 재번호해도 안 흔들리게).
 * 2. **`serverToday`** — 같은 회차 응답에서 파생된 날짜(`findToday(round)?.date`). 1번이 못 찾은
 *    값을 같은 소스에서 다시 만들 수는 없으므로 방어 분기다(`todayDayIndex` 가 배열에 없는 경우).
 *
 * **"회차 데이터로 추측"하는 3순위는 두지 않는다.** `[완주, 빈칸, …]` 만으로는 "어제를 완주했다"와
 * "오늘을 완주했다"를 구분할 수 없어서, 완주 직후에도 스탬프판이 다음 칸을 오늘로 짚는 경로가
 * 생긴다. 되살리지 마라. 둘 다 없으면 오늘을 정하지 않는다(`undefined`) — 그래도 CTA 는 **열린
 * 채** 남는다(fail-open). 첫 출석이 회차를 여는 동작이라 오늘을 못 정했다고 버튼을 막으면
 * 사용자가 영원히 시작할 수 없다.
 */
export function resolveTodayPosition(
  round: DailyCheckInRound | undefined,
  serverToday?: string | null,
): number | undefined {
  if (!round?.isActive || round.days.length === 0) return undefined;

  if (round.todayDayIndex !== undefined) {
    const byDayIndex = round.days.findIndex((day) => day.dayIndex === round.todayDayIndex);
    if (byDayIndex >= 0) return byDayIndex;
  }

  if (serverToday) {
    const byServerDate = round.days.findIndex((day) => day.date === serverToday);
    if (byServerDate >= 0) return byServerDate;
    // 회차 밖의 날짜다 — 회차가 바뀐 뒤의 낡은 값이라 버린다.
  }

  return undefined;
}

/** 회차에서 오늘로 판정된 하루. */
export function findToday(
  round: DailyCheckInRound | undefined,
  serverToday?: string | null,
): DailyCheckInDay | undefined {
  const at = resolveTodayPosition(round, serverToday);
  return at === undefined ? undefined : round?.days[at];
}

/**
 * 오늘 **한 번이라도** 출석했는가 — 오전·오후 어느 슬롯이든, 복구로 되살린 날이든 true.
 *
 * CTA 잠금(`toAttendanceSection` 의 `isTodayAttendanceLocked`)과 **다른 질문**이다. 잠금은 "지금 열린
 * 슬롯이 채워졌는가"라 오전 출석 뒤 오후 슬롯이 열리면 풀리지만, 이쪽은 하루 단위라 오전 출석이 하루
 * 종일 유지된다(메인 `AuthBox` 문구). 둘을 합치지 마라.
 *
 * DTO 정의상 `isCheckedIn` 하나면 충분하지만(한 슬롯만 채워도 true, 복구한 날도 true) `streakStatus` 와
 * 같은 이유로 슬롯 플래그도 함께 본다 — `isCheckedIn` 이 뒤처진 응답에서 조용히 false 가 되지 않게.
 * 오늘을 못 정하면(회차 없음·응답 전·`todayDayIndex` 불일치) false 다.
 */
export function hasCheckedInToday(
  round: DailyCheckInRound | undefined,
  serverToday?: string | null,
): boolean {
  const today = findToday(round, serverToday);
  if (!today) return false;
  return today.isCheckedIn || today.isMorningCheckedIn || today.isAfternoonCheckedIn;
}
